import { ref, computed, watch } from "vue";
import { defineStore } from "pinia";
import type { NotificationDocument } from "@kekkle/shared/types/documents/notification-document/notification-document";
import {
  subscribeToUserNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotificationDoc,
} from "@/helpers/firestore/NotificationService";
import {
  getCachedDocuments,
  setCachedDocuments,
} from "@/helpers/cache/document-cache";
import { useAuthStore } from "@/stores/modules/auth/auth";
import type { Unsubscribe } from "firebase/firestore";

export const useInAppNotificationsStore = defineStore(
  "in-app-notifications",
  () => {
    const authStore = useAuthStore();

    /* State */
    const notifications = ref<NotificationDocument[]>([]);
    const isLoading = ref<boolean>(false);
    const isStale = ref<boolean>(false);
    const hasServerResponded = ref<boolean>(false);
    let unsubscribe: Unsubscribe | null = null;

    /* Computed */
    const unreadCount = computed<number>(() => {
      return notifications.value.filter((n) => !n.is_read).length;
    });

    const unreadNotifications = computed<NotificationDocument[]>(() => {
      return notifications.value.filter((n) => !n.is_read);
    });

    /* Actions */
    function initListener() {
      const uid = authStore.authUuid;
      if (!uid) {
        cleanup();
        return;
      }

      if (unsubscribe) {
        return; // Already subscribed
      }

      hasServerResponded.value = false;

      // If we don't have in-memory notifications, show loading and hydrate from cache
      const hasInMemory = notifications.value.length > 0;
      if (!hasInMemory) {
        isLoading.value = true;
        // Immediately read from IndexedDB cache to populate the store instantaneously
        getCachedDocuments<NotificationDocument>("notifications", uid)
          .then((cached) => {
            // Only populate from cache if server hasn't already responded
            if (cached && cached.length > 0 && !hasServerResponded.value) {
              notifications.value = cached;
              isLoading.value = false;
              isStale.value = true;
            }
          })
          .catch((error) => {
            console.error(
              "Error reading cached notifications from IndexedDB:",
              error,
            );
          });
      }

      unsubscribe = subscribeToUserNotifications(
        uid,
        (docs, metadata) => {
          notifications.value = docs;
          isLoading.value = false;

          if (!metadata.fromCache) {
            // Fresh server response received
            hasServerResponded.value = true;
            isStale.value = false;
          } else if (!hasServerResponded.value) {
            // Cached response from Firestore local cache
            isStale.value = true;
          }

          // Persist fresh data to IndexedDB
          setCachedDocuments("notifications", uid, docs).catch((error) => {
            console.error(
              "Error saving notifications to IndexedDB cache:",
              error,
            );
          });
        },
        () => {
          isLoading.value = false;
        },
      );
    }

    function cleanup() {
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }
      notifications.value = [];
      isLoading.value = false;
      isStale.value = false;
      hasServerResponded.value = false;
    }

    async function markAsRead(notificationId: string): Promise<void> {
      const uid = authStore.authUuid;
      if (!uid) return;

      // Optimistic update
      const target = notifications.value.find((n) => n._id === notificationId);
      if (target && !target.is_read) {
        target.is_read = true;
        target.read_at = new Date();
      }

      await markNotificationAsRead(uid, notificationId);
    }

    async function markAllAsRead(): Promise<void> {
      const uid = authStore.authUuid;
      if (!uid) return;

      const unreadIds = notifications.value
        .filter((n) => !n.is_read)
        .map((n) => n._id);

      if (unreadIds.length === 0) return;

      // Optimistic update
      const now = new Date();
      for (const n of notifications.value) {
        if (!n.is_read) {
          n.is_read = true;
          n.read_at = now;
        }
      }

      await markAllNotificationsAsRead(uid, unreadIds);
    }

    async function deleteNotification(notificationId: string): Promise<void> {
      const uid = authStore.authUuid;
      if (!uid) return;

      // Optimistic update
      notifications.value = notifications.value.filter(
        (n) => n._id !== notificationId,
      );

      await deleteNotificationDoc(uid, notificationId);
    }

    // Automatically manage listener on auth change
    watch(
      () => authStore.authUuid,
      (newUid) => {
        if (newUid) {
          initListener();
        } else {
          cleanup();
        }
      },
      { immediate: true },
    );

    return {
      notifications,
      unreadCount,
      unreadNotifications,
      isLoading,
      isStale,
      initListener,
      cleanup,
      markAsRead,
      markAllAsRead,
      deleteNotification,
    };
  },
);
